import { isValidCepFormat, normalizeCep } from '@aeris/party'

/**
 * Buscar um endereço pelo CEP — F089.
 *
 * ## A decisão que isto revê
 *
 * O formulário de endereço nasceu (F011) com um comentário explícito dizendo o
 * contrário: *"No CEP lookup: an address form that cannot be completed while
 * the network is down is one that stops a sale"*. A razão continua de pé — a
 * seção 25 faz de "offline" um estado de operação suportado, não um acidente.
 *
 * O que muda é o PAPEL da consulta. Ela não valida nada, não trava campo
 * nenhum e não é obrigatória: preenche o que está vazio e sai do caminho.
 * Falhou, ficou sem rede, o CEP não existe? Os campos continuam editáveis, o
 * botão de salvar continua ativo, e quem está com o cliente na frente digita o
 * endereço como digitava antes. Um assistente, não uma etapa.
 *
 * ## Por que direto do navegador
 *
 * O ViaCEP responde com `Access-Control-Allow-Origin: *` e
 * `Cache-Control: max-age=3600`, então o navegador fala com ele sem
 * intermediário e ainda reaproveita a resposta. Passar pela API do Aeris
 * significaria a API da loja precisando de saída para a internet — que é
 * justamente o que muitas instalações não têm — para uma consulta que não
 * envolve dado nenhum do tenant. Um CEP não é dado pessoal: é um código
 * postal, sem nome nem documento junto.
 *
 * ## O aviso que o ViaCEP publica
 *
 * > Uso massivo para validação de bases de dados locais poderá automaticamente
 * > bloquear seu acesso.
 *
 * Por isso a consulta só dispara com OITO dígitos completos, o resultado é
 * guardado em memória por sessão, e o mesmo CEP nunca é perguntado duas vezes.
 * **Nada disto entra em importação em massa** — importar mil clientes seria
 * exatamente o uso que o aviso proíbe.
 */

export type CepAddress = {
  street: string
  district: string
  city: string
  uf: string
  /** Código IBGE do município — a coluna existe desde a 0006 e vivia nula. */
  ibgeCode: string
}

/**
 * O que o ViaCEP devolve, do jeito que ele devolve.
 *
 * `erro` chega como a STRING `"true"` na documentação vigente, e há instalações
 * antigas devolvendo o booleano. As duas formas são tratadas, porque a
 * alternativa é a tela preencher o endereço com cinco campos vazios e um código
 * IBGE em branco e parecer que funcionou.
 */
type ViaCepResponse = {
  logradouro?: unknown
  bairro?: unknown
  localidade?: unknown
  uf?: unknown
  ibge?: unknown
  erro?: unknown
}

const textOf = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

const CACHE = new Map<string, CepAddress | null>()

export const CEP_ENDPOINT = 'https://viacep.com.br/ws'

/**
 * `null` para tudo que não seja um endereço utilizável — CEP inexistente, rede
 * fora, resposta estranha. Nunca lança: quem chama está no meio de um
 * formulário, e uma exceção aqui derrubaria a tela por causa de um assistente.
 */
export const lookupCep = async (
  cep: string,
  signal?: AbortSignal
): Promise<CepAddress | null> => {
  const digits = normalizeCep(cep)
  if (!isValidCepFormat(digits)) return null

  const cached = CACHE.get(digits)
  if (cached !== undefined) return cached

  try {
    const response = await fetch(
      `${CEP_ENDPOINT}/${digits}/json/`,
      signal === undefined ? {} : { signal }
    )
    // 400 é o que ele responde a um CEP malformado; qualquer outro código é
    // problema dele, e nos dois casos a resposta é a mesma: seguir sem.
    if (!response.ok) return null

    const body = (await response.json()) as ViaCepResponse
    if (body.erro === true || body.erro === 'true') {
      // CEP que não existe é resposta CONHECIDA, e vale guardar: quem digitou
      // errado costuma corrigir e voltar.
      CACHE.set(digits, null)
      return null
    }

    const address: CepAddress = {
      street: textOf(body.logradouro),
      district: textOf(body.bairro),
      city: textOf(body.localidade),
      uf: textOf(body.uf).toUpperCase(),
      ibgeCode: textOf(body.ibge),
    }

    /*
     * Sem cidade e sem UF não há endereço nenhum a oferecer — alguns CEPs
     * genéricos vêm com logradouro vazio, e isso é normal (o número da rua é
     * que completa), mas município vazio significa resposta imprestável.
     */
    if (address.city === '' || address.uf === '') {
      CACHE.set(digits, null)
      return null
    }

    CACHE.set(digits, address)
    return address
  } catch {
    /*
     * Rede fora, DNS, CORS, `AbortError` de quem continuou digitando. Nada
     * disso é guardado no cache: a próxima tentativa deve tentar de novo.
     */
    return null
  }
}

/**
 * O que a busca pode sobrescrever, e o que ela nunca toca — F089.
 *
 * Um campo só é preenchido quando está **vazio** ou quando ainda tem
 * exatamente o valor que a busca ANTERIOR pôs nele. É essa segunda condição
 * que permite corrigir um CEP digitado errado — a correção troca o endereço
 * inteiro — sem que ela apague o que a pessoa escreveu por cima.
 *
 * Fora daqui porque é a regra mais fácil de quebrar da feature, e uma regra
 * assim dentro de um `setState` no meio de um formulário é uma regra que
 * nenhum teste alcança.
 *
 * Trabalha só sobre `CepAddress`, e não sobre o formulário inteiro por um
 * genérico: a UF do formulário é um conjunto fechado de siglas, e um genérico
 * escreveria a string crua do serviço dentro dele sem que o compilador
 * reclamasse. Quem estreita é o chamador, DEPOIS de conferir.
 */
export const mergeCepInto = (
  current: CepAddress,
  found: CepAddress,
  previous: CepAddress | null
): CepAddress => {
  const merged = { ...current }
  for (const field of [
    'street',
    'district',
    'city',
    'ibgeCode',
    'uf',
  ] as const) {
    const typed = current[field]
    const wasFilledByLookup =
      previous !== null && typed === previous[field] && typed !== ''
    if (typed.trim() === '' || wasFilledByLookup) {
      merged[field] = found[field]
    }
  }
  return merged
}
